<script setup lang="ts">
import { cards } from '~/data/products';
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

let blurObserver: IntersectionObserver | null = null;

const wrapperRef = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
const isPinned = ref(false);
type PinPhase = 'before' | 'during' | 'after';
const pinPhase = ref<PinPhase>('before');
const prevIndex = ref<number | null>(null);
const FADE_MS = 1000;
let fadeTimer: number | null = null;
const updatePinned = () => {
  isPinned.value =
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 1024px)').matches;
};

const onScroll = () => {
  if (!isPinned.value || !wrapperRef.value) return;
  const el = wrapperRef.value;
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const totalScrollable = Math.max(0, rect.height - vh);
  // сколько "прокручено" внутри пин-секции (0..totalScrollable)
  const scrolled = Math.min(Math.max(-rect.top, 0), totalScrollable);
  const progress = totalScrollable > 0 ? scrolled / totalScrollable : 0;
  // индекс активной карточки
  const idx = Math.min(cards.length - 1, Math.floor(progress * cards.length));
  if (idx !== activeIndex.value) activeIndex.value = idx;

  // Определяем фазу пина: до, во время, после
  // Отпускаем fixed под конец, чтобы последняя карточка прокручивалась как обычный блок — без рывка

  let phase: PinPhase = 'during';
  if (rect.top > 0) phase = 'before';
  else if (progress >= 1) phase = 'after';
  if (pinPhase.value !== phase) pinPhase.value = phase;
};

const onResize = () => {
  updatePinned();
 
  requestAnimationFrame(onScroll);
};

onMounted(() => {
  updatePinned();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  requestAnimationFrame(onScroll);

    blurObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
        }
      });
    },
    {
      threshold: 0.4,
    },
  );

  const title = document.querySelector<HTMLElement>('.title-blur');
  if (title) blurObserver?.observe(title);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onResize);
  if (fadeTimer) {
    clearTimeout(fadeTimer);
    fadeTimer = null;
  }
});

watch(activeIndex, (next, prev) => {
  if (!isPinned.value) return;
  if (next === prev) return;
  prevIndex.value = prev;
  if (fadeTimer) clearTimeout(fadeTimer);
  fadeTimer = window.setTimeout(() => {
    prevIndex.value = null;
    fadeTimer = null;
  }, FADE_MS + 50);
});
</script>

<template>
  <section
    id="products"
    class="products-wrapper"
    ref="wrapperRef"
    :style="isPinned ? { height: `${cards.length * 100}vh` } : {}"
  >
    <div
      class="products-pin-inner"
      :class="{
        'products-pin-inner--pinned': isPinned,
        'products-pin-inner--fixed': isPinned && pinPhase === 'during',
        'products-pin-inner--abs-top': isPinned && pinPhase === 'before',
        'products-pin-inner--abs-bottom': isPinned && pinPhase === 'after',
      }"
    >
      <div class="products">
        <h2 class="title headline-2 title-blur">Live Products</h2>

        <div
          class="cards-wrapper"
          :class="{ 'cards-wrapper--pinned': isPinned }"
        >
          <div
            v-for="(card, i) in cards"
            :key="card.title"
            class="card"
            :class="{
              active: i === activeIndex,
              leaving: prevIndex !== null && i === prevIndex,
            }"
          >
            <div class="card-content">
              <h3 class="card-title font-subtitle">{{ card.title }}</h3>
              <p class="card-text font-title">{{ card.text_1 }}</p>
              <p v-if="card?.text_2" class="card-text font-title">
                {{ card.text_2 }}
              </p>

              <ul v-if="card?.features?.length" class="card-list">
                <li
                  v-for="(feature, fi) in card.features"
                  :key="fi"
                  class="card-item font-bodytext"
                >
                  <icon
                    class="card-bullet"
                    size="22px"
                    name="icons:products-bullet"
                  />
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div class="card-image-wrapper">
              <nuxt-img
                :src="card.image"
                :alt="card.title"
                class="card-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.products-wrapper {
  position: relative;
  width: 100%;
  display: block;
}

.products-pin-inner {
  position: static;
}
.products-pin-inner--pinned {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--v2-bg);
  z-index: 2;
}

.products-pin-inner--fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
}

.products-pin-inner--abs-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
}
.products-pin-inner--abs-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
}

.products {
  width: 100%;
  max-width: 1440px;
  padding: 60px 40px 60px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 60px;
}

.title {
  width: 100%;
  text-align: center;
  text-transform: uppercase;
}

.title-blur {
  filter: blur(4px);
  transition: filter .5s ease;
}

.title-blur.is-visible {
  filter: blur(0);
}

.cards-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 60px;
}

.cards-wrapper--pinned {
  position: relative;
  gap: 0;
  width: 100%;
  height: calc(100vh - 60px - 60px - 144px - 143px);
}

.cards-wrapper--pinned .card {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  opacity: 0;
  filter: blur(10px);
  transform: none;
  transition: opacity 1000ms ease, filter 500ms ease;
  pointer-events: none;
  z-index: 0;
  will-change: opacity, filter;
  backface-visibility: hidden;
}
.cards-wrapper--pinned .card.active {
  opacity: 1;
  filter: blur(0);
  transform: none;
  pointer-events: auto;
  z-index: 2;
}
.cards-wrapper--pinned .card.leaving {
  opacity: 0;
  filter: blur(10px);
  transform: none;
  z-index: 1;
}

/* Базовые карточки (не pinned) */
.card {
  width: 100%;
  height: 513px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.card-content {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card-title {
  width: 100%;
  text-align: left;
  text-transform: uppercase;
  color: var(--main-blue);
  margin-bottom: 12px;
}

.card-text {
  width: 100%;
  text-align: left;
  margin-bottom: 12px;
}

.card-text:last-of-type {
  margin-bottom: 40px;
}

.card-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.card-item {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
}

.card-bullet {
  filter: blur(0.7px);
  flex-shrink: 0;
}

@media (max-width: 1200px) {
  .products {
    padding: 60px 20px 60px;
  }

  .card {
    align-items: flex-start;
    height: auto;
  }

  .card-list {
    gap: 10px;
  }
}

@media (max-width: 1023px) {
  /* show simple cards on mobile */
  .products-pin-inner--pinned {
    position: static;
    height: auto;
  }
  .products-pin-inner--fixed,
  .products-pin-inner--abs-top,
  .products-pin-inner--abs-bottom {
    position: static;
    height: auto;
  }
  .cards-wrapper--pinned {
    position: static;
    height: auto;
    overflow: visible;
  }
  .cards-wrapper--pinned .card {
    position: static;
    opacity: 1;
    transform: none;
    pointer-events: auto;
    transition: none;
    margin-bottom: 32px;
  }

  .products {
    gap: 32px;
    padding: 30px 20px 30px;
  }

  .card {
    height: auto;
    flex-direction: column;
    gap: 12px;
  }

  .card-text {
    margin-bottom: 6px;
  }

  .card-text:last-of-type {
    margin-bottom: 16px;
  }

  .card-content {
    max-width: 100%;
  }

  .card-list {
    gap: 5px;
  }

  .card-title {
    margin-bottom: 8px;
  }
}

@media (max-width: 743px) {
  .card {
    gap: 14px;
  }

  .card-list {
    gap: 8px;
  }

  .cards-wrapper {
    gap: 32px;
  }

  .card-text {
    margin-bottom: 9px;
  }
}
</style>
